import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { AlertTriangle, FileSignature, ArrowRight, Clock } from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';

const SNOOZE_HORAS = 24;
const COUNTDOWN_SEGUNDOS = 10;

export const ContratoPendenteModal = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [contratosPendentes, setContratosPendentes] = useState([]);
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SEGUNDOS);

  const snoozeKey = `contrato_pendente_snooze_${user?.id}`;

  useEffect(() => {
    if (user?.perfil !== 'profissional') return;

    // Verificar snooze de 24h
    const snooze = localStorage.getItem(snoozeKey);
    if (snooze && Date.now() - parseInt(snooze, 10) < SNOOZE_HORAS * 60 * 60 * 1000) {
      return;
    }

    const checkPendencias = async () => {
      try {
        const res = await authFetch('/contratos?status=pendente_aceite', {
          suppressAuthRedirect: true,
          timeout: 10000
        });
        const pendentes = (res.data || []).filter(c => c.status_calculado === 'pendente_aceite');
        if (pendentes.length > 0) {
          setContratosPendentes(pendentes);
          setVisible(true);
          setCountdown(COUNTDOWN_SEGUNDOS);
        }
      } catch (err) {
        console.error('Erro ao verificar contratos pendentes:', err);
      }
    };
    checkPendencias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.perfil, user?.id]);

  // Contagem regressiva para liberar o botão "Lembrar amanhã"
  useEffect(() => {
    if (!visible || countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [visible, countdown]);

  // Bloquear scroll do body enquanto visível
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [visible]);

  if (!visible) return null;

  const handleResolverAgora = () => {
    // NÃO grava snooze: se não aceitar, o modal volta no próximo acesso ao Dashboard
    setVisible(false);
    navigate('/dashboard/contratos');
  };

  const handleLembrarAmanha = () => {
    localStorage.setItem(snoozeKey, Date.now().toString());
    setVisible(false);
  };

  const diasPendente = (contrato) => {
    try {
      return differenceInDays(new Date(), parseISO(contrato.criado_em));
    } catch {
      return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(69, 10, 10, 0.97)' }}
      data-testid="contrato-pendente-overlay"
    >
      <div className="max-w-xl w-full text-center space-y-6">
        <AlertTriangle
          className="h-20 w-20 mx-auto animate-pulse"
          style={{ color: '#FCA5A5' }}
        />

        <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
          VOCÊ TEM {contratosPendentes.length > 1 ? `${contratosPendentes.length} CONTRATOS PENDENTES` : 'CONTRATO PENDENTE'} DE ACEITE
        </h1>

        <p className="text-base" style={{ color: '#FECACA' }}>
          A regularização do aceite do seu contrato com a clínica é <strong>obrigatória</strong>.
          Este aviso continuará aparecendo até que a pendência seja resolvida.
        </p>

        {/* Lista de contratos pendentes */}
        <div className="space-y-2 text-left">
          {contratosPendentes.map((c) => {
            const dias = diasPendente(c);
            return (
              <div
                key={c.id}
                className="flex items-center justify-between rounded-lg px-4 py-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(252,165,165,0.3)' }}
                data-testid={`contrato-pendente-item-${c.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileSignature className="h-5 w-5 shrink-0" style={{ color: '#FCA5A5' }} />
                  <span className="text-sm font-medium text-white truncate">
                    {c.arquivo_nome || 'Contrato com a Clínica'}
                  </span>
                </div>
                {dias !== null && (
                  <span
                    className="text-xs font-bold shrink-0 ml-3 px-2 py-1 rounded-full"
                    style={{ backgroundColor: '#991B1B', color: '#FECACA' }}
                  >
                    Pendente há {dias} {dias === 1 ? 'dia' : 'dias'}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-3 pt-2">
          <Button
            onClick={handleResolverAgora}
            className="w-full h-14 text-lg font-bold text-white animate-pulse"
            style={{ backgroundColor: '#16A34A' }}
            data-testid="contrato-resolver-agora-btn"
          >
            RESOLVER AGORA
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>

          <Button
            onClick={handleLembrarAmanha}
            disabled={countdown > 0}
            variant="ghost"
            className="w-full text-sm"
            style={{ color: countdown > 0 ? 'rgba(254,202,202,0.4)' : '#FECACA' }}
            data-testid="contrato-lembrar-amanha-btn"
          >
            {countdown > 0 ? (
              <>
                <Clock className="h-4 w-4 mr-2" />
                Lembrar amanhã (aguarde {countdown}s)
              </>
            ) : (
              'Lembrar amanhã'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
